import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { MapPin, Loader2, Sparkles, Heart, RefreshCcw, Shield, Radio, RadioTower } from "lucide-react";
import { toast } from "sonner";

const MOODS = [
  { key: "grateful", label: "Grateful", emoji: "🙏" },
  { key: "hopeful", label: "Hopeful", emoji: "✨" },
  { key: "peaceful", label: "Peaceful", emoji: "🌿" },
  { key: "curious", label: "Curious", emoji: "🔎" },
  { key: "playful", label: "Playful", emoji: "🌤" },
];

const fmtRel = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function CrossedPaths() {
  const [pos, setPos] = useState(null); // {lat, lng}
  const [posBusy, setPosBusy] = useState(false);
  const [nearby, setNearby] = useState([]);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [mood, setMood] = useState("hopeful");
  const [placeName, setPlaceName] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [radius, setRadius] = useState(1200);
  // Live Presence Beacon
  const [live, setLive] = useState(false);
  const [liveList, setLiveList] = useState([]);
  const heartbeatRef = useRef(null);
  const liveFetchRef = useRef(null);

  const stopLive = async () => {
    if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
    if (liveFetchRef.current) { clearInterval(liveFetchRef.current); liveFetchRef.current = null; }
    try { await api.post("/paths/leave"); } catch (_) {}
    setLive(false);
    setLiveList([]);
  };

  const startLive = async () => {
    if (!pos) { toast.error("Share your location first."); return; }
    setLive(true);
    const beat = async () => {
      try {
        // refresh position each beat for accuracy
        const p = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 8000, maximumAge: 20000 })
        );
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude });
        await api.post("/paths/heartbeat", { lat: p.coords.latitude, lng: p.coords.longitude });
      } catch (e) { /* ignore transient */ }
    };
    const fetchLive = async () => {
      try {
        const { data } = await api.get(`/paths/live`, { params: { lat: pos.lat, lng: pos.lng, radius_m: 500 } });
        setLiveList(data.live || []);
      } catch (_) {}
    };
    await beat();
    await fetchLive();
    heartbeatRef.current = setInterval(beat, 30000);
    liveFetchRef.current = setInterval(fetchLive, 30000);
    toast.success("You're live nearby. Kind souls within 500m will see you.");
  };

  useEffect(() => {
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (liveFetchRef.current) clearInterval(liveFetchRef.current);
      // best-effort leave on unmount
      api.post("/paths/leave").catch(() => {});
    };
  }, []);

  const requestLocation = () => {
    setPosBusy(true);
    setErr("");
    if (!("geolocation" in navigator)) {
      setErr("Your browser doesn't support location.");
      setPosBusy(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude });
        setPosBusy(false);
      },
      (e) => {
        setErr(e.message || "Location denied.");
        setPosBusy(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const loadNearby = async () => {
    if (!pos) return;
    try {
      const { data } = await api.get(`/places/nearby`, { params: { lat: pos.lat, lng: pos.lng, radius_m: radius } });
      setNearby(data.nearby || []);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  useEffect(() => {
    if (pos) loadNearby();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, radius]);

  const dropMoment = async () => {
    if (!pos || note.trim().length < 3) return;
    setSaveBusy(true);
    try {
      await api.post("/places/moments", {
        lat: pos.lat,
        lng: pos.lng,
        place_name: placeName.trim() || null,
        note: note.trim(),
        mood,
      });
      toast.success("Moment left here. Someone kind may find it.");
      setNote("");
      setPlaceName("");
      await loadNearby();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaveBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="crossed-paths-page">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-ochre-300 font-mono">Serendipity, safely</div>
        <h1 className="font-display text-5xl text-ink leading-tight mt-2" style={{ fontFamily: "Fraunces", fontWeight: 400 }}>
          Crossed <em className="italic text-ivy">Paths.</em>
        </h1>
        <p className="text-ink/70 mt-3 max-w-2xl">
          Leave a wholesome thought at a place. Days later, someone kind who wandered through might find it.
          No tracking, no faces, no real-time location — just gentle serendipity.
        </p>
      </div>

      {/* Privacy notice */}
      <div className="card-paper p-5 flex items-start gap-3 border-l-4 border-ivy" data-testid="paths-privacy">
        <Shield size={18} className="text-ivy mt-0.5 flex-shrink-0" />
        <div className="text-sm text-ink/75 leading-relaxed">
          <strong>Privacy promise.</strong> Locations are snapped to a rough 110-metre grid — never precise. You choose when
          to share your location. Nobody else can see where you are.
        </div>
      </div>

      {/* Location gate */}
      {!pos && (
        <div className="card-paper p-8 text-center" data-testid="paths-need-location">
          <MapPin size={28} className="mx-auto text-ivy" />
          <h3 className="font-display text-2xl text-ink mt-3" style={{ fontFamily: "Fraunces" }}>
            Share your location once to begin.
          </h3>
          <p className="text-ink/60 text-sm mt-2">We only use it to check what's nearby right now.</p>
          <button
            data-testid="paths-share-location"
            onClick={requestLocation}
            disabled={posBusy}
            className="btn-pill bg-ivy text-parchment hover:bg-ivy-600 mt-5 disabled:opacity-50 flex items-center gap-2 mx-auto"
          >
            {posBusy ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />}
            {posBusy ? "Finding you…" : "Share location"}
          </button>
          {err && <div className="text-sm text-ochre-300 mt-4">{err}</div>}
        </div>
      )}

      {pos && (
        <>
          {/* Drop a moment */}
          <div className="card-paper p-6" data-testid="paths-drop-form">
            <div className="eyebrow">Leave a moment here</div>
            <div className="mt-3 space-y-3">
              <input
                data-testid="paths-place-name"
                value={placeName}
                onChange={(e) => setPlaceName(e.target.value)}
                placeholder="Place name (optional) — e.g. Bondi Icebergs"
                className="w-full px-4 py-3 rounded-2xl bg-parchment-50 border border-ink/10"
              />
              <textarea
                data-testid="paths-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 280))}
                rows={3}
                placeholder="A kind thought for whoever finds this next…"
                className="w-full px-4 py-3 rounded-2xl bg-parchment-50 border border-ink/10 resize-none"
              />
              <div className="flex flex-wrap gap-2">
                {MOODS.map((m) => (
                  <button
                    key={m.key}
                    data-testid={`paths-mood-${m.key}`}
                    onClick={() => setMood(m.key)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all duration-500 ${
                      mood === m.key
                        ? "bg-ivy text-parchment border-ivy"
                        : "bg-white text-ink border-ink/10 hover:bg-parchment-50"
                    }`}
                  >
                    <span className="mr-1">{m.emoji}</span>
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-ink/40 font-mono">{note.length}/280</span>
                <button
                  data-testid="paths-drop-btn"
                  onClick={dropMoment}
                  disabled={saveBusy || note.trim().length < 3}
                  className="btn-pill bg-ink text-parchment hover:bg-ivy disabled:opacity-50 flex items-center gap-2"
                >
                  <Sparkles size={14} /> {saveBusy ? "Leaving…" : "Leave moment"}
                </button>
              </div>
            </div>
          </div>

          {/* Live Presence Beacon */}
          <div className={`card-paper p-6 ${live ? "border-2 border-ivy" : ""}`} data-testid="live-beacon-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="eyebrow flex items-center gap-2">
                  {live ? <RadioTower size={12} className="text-ivy animate-pulse" /> : <Radio size={12} />}
                  Live Presence Beacon
                </div>
                <h3 className="font-display text-2xl text-ink mt-1" style={{ fontFamily: "Fraunces" }}>
                  {live ? <>You're <em className="italic text-ivy">live</em> right now.</> : <>Feel like being <em className="italic text-ivy">found?</em></>}
                </h3>
                <p className="text-sm text-ink/60 mt-2 max-w-md">
                  {live
                    ? "Other Wholesome members within 500m will see that a licensed member is nearby — never who or where exactly. Auto-expires in 5 minutes."
                    : "Turn on the beacon. Anyone within 500m who's also live sees a soft signal. No profiles, no names, no chat — just the possibility of a real world crossing."}
                </p>
              </div>
              <button
                data-testid={live ? "live-stop-btn" : "live-start-btn"}
                onClick={live ? stopLive : startLive}
                className={`btn-pill flex items-center gap-2 ${
                  live ? "bg-ochre text-parchment hover:bg-ochre-600" : "bg-ivy text-parchment hover:bg-ivy-600"
                }`}
              >
                {live ? <><Radio size={14} /> Go quiet</> : <><RadioTower size={14} /> Go live</>}
              </button>
            </div>
            {live && (
              <div className="mt-5 pt-5 border-t border-ochre-100">
                <div className="text-xs uppercase tracking-widest text-ochre-300 font-mono">
                  {liveList.length === 0 ? "Nobody nearby yet" : `${liveList.length} ${liveList.length === 1 ? "member is" : "members are"} live within 500m`}
                </div>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="live-list">
                  {liveList.map((m) => (
                    <div key={m.license_no} data-testid={`live-${m.license_no}`} className="px-3 py-2 rounded-full bg-parchment-50 border border-ivy/20 flex items-center gap-2 text-xs">
                      <span className="w-2 h-2 rounded-full bg-ivy animate-pulse" />
                      <span className="font-mono text-ink">{m.license_no}</span>
                      <span className="text-ink/50">· ~{m.distance_m}m</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-[11px] text-ink/50 italic">
                  Note — this is our GPS-based beacon. True Bluetooth crossings will arrive with the Wholesome mobile app.
                </div>
              </div>
            )}
          </div>

          {/* Nearby moments */}
          <div className="flex items-center justify-between">
            <div>
              <div className="eyebrow">What's near you</div>
              <h3 className="font-display text-2xl text-ink mt-1" style={{ fontFamily: "Fraunces" }}>
                Moments within {(radius / 1000).toFixed(1)}km
              </h3>
            </div>
            <div className="flex gap-2 items-center">
              <select
                data-testid="paths-radius"
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                className="px-3 py-2 rounded-full text-sm bg-white border border-ink/10"
              >
                <option value={500}>500m</option>
                <option value={1200}>1.2km</option>
                <option value={3000}>3km</option>
                <option value={10000}>10km</option>
              </select>
              <button
                data-testid="paths-refresh"
                onClick={loadNearby}
                className="p-2 rounded-full bg-white border border-ink/10 hover:bg-parchment-50"
                title="Refresh"
              >
                <RefreshCcw size={14} className="text-ink" />
              </button>
            </div>
          </div>

          <div className="space-y-3" data-testid="paths-nearby-list">
            {nearby.length === 0 && (
              <div className="text-ink/50 italic text-center py-8" data-testid="paths-empty">
                Nothing wholesome here yet. Be the first — leave a moment.
              </div>
            )}
            {nearby.map((m) => (
              <div key={m.id} className="card-paper p-5" data-testid={`paths-item-${m.id}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-ochre-300">
                    <MapPin size={12} />
                    {m.place_name || "somewhere near you"}
                    <span className="text-ink/40">· {m.distance_m}m away</span>
                  </div>
                  <div className="text-xs text-ink/50 font-mono">{fmtRel(m.at)}</div>
                </div>
                <blockquote className="mt-3 font-display italic text-ink text-lg leading-relaxed" style={{ fontFamily: "Fraunces" }}>
                  "{m.note}"
                </blockquote>
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-xs text-ink/50">
                    {m.own ? (
                      <span className="text-ivy font-medium">
                        <Heart size={11} className="inline mr-1" /> you left this
                      </span>
                    ) : (
                      <span>
                        A Wholesome member · <span className="font-mono">{m.author_license_no || "License holder"}</span>
                      </span>
                    )}
                  </div>
                  {m.mood && <div className="text-xs text-ivy italic capitalize">{m.mood}</div>}
                </div>
              </div>
            ))}
          </div>

          {err && (
            <div className="text-sm text-ochre-300" data-testid="paths-error">
              {err}
            </div>
          )}
        </>
      )}
    </div>
  );
}
